import type { UiNamespace } from "./resources.js";
import type { UiLocale } from "@tubo/api-models/business/common/ui-locale";
import type { BackendModule } from "i18next";
import type { uiCoreResources } from "./core-resources.js";

type ResourceRecord = Record<string, unknown>;
type BilingualResource = Record<UiLocale, ResourceRecord>;
type ResourceModule = Record<string, unknown>;
type PreloadedNamespace = keyof (typeof uiCoreResources)["zh-CN"];
type LazyNamespace = Exclude<UiNamespace, PreloadedNamespace>;

const namespaceResourceModules: Record<LazyNamespace, readonly string[]> = {
  docCenter: [
    "./doc-center-resources.ts",
    "./doc-center-preview-resources.ts",
    "./doc-center-editor-resources.ts",
    "./doc-center-operations-resources.ts",
  ],
  packTree: ["./doc-center-resources.ts"],
  docGeneration: [
    "./doc-generation-resources.ts",
    "./doc-generation-advanced-resources.ts",
    "./doc-center-operations-resources.ts",
  ],
  filePreview: [
    "./file-preview-resources.ts",
    "./file-preview-excel-resources.ts",
  ],
  tenantAdmin: ["./tenant-admin-resources.ts"],
  adminJob: ["./admin-job-resources.ts"],
  botCatalog: ["./bot-catalog-resources.ts"],
  botStudio: ["./bot-studio-resources.ts"],
  botRun: ["./bot-run-resources.ts"],
  auxiliaryPages: ["./auxiliary-pages-resources.ts"],
  accountSettingsPage: ["./settings-core-resources.ts"],
  systemSettingsNavigation: ["./settings-core-resources.ts"],
  settingsMobileNavigation: ["./settings-core-resources.ts"],
  tenantSettings: ["./tenant-settings-resources.ts"],
  tenantSettingsData: ["./tenant-settings-data-resources.ts"],
  systemSettings: [
    "./system-settings-resources.ts",
    "./system-settings-advanced-resources.ts",
  ],
  truth: ["./truth-resources.ts"],
  platformManagement: ["./platform-management-resources.ts"],
  dataCenter: [
    "./data-center-resources.ts",
    "./data-center-review-resources.ts",
    "./data-center-template-resources.ts",
  ],
  dataExtraction: ["./data-extraction-resources.ts"],
  recycleBin: ["./recycle-bin-resources.ts"],
  knowledgeBase: [
    "./knowledge-base-resources.ts",
    "./knowledge-base-ingestion-resources.ts",
    "./knowledge-base-topic-resources.ts",
    "./knowledge-base-settings-resources.ts",
    "./knowledge-base-document-resources.ts",
    "./knowledge-base-project-resources.ts",
    "./knowledge-base-settings-collaboration-resources.ts",
    "./knowledge-base-compilation-resources.ts",
  ],
  semanticPlatform: [
    "./semantic-platform-resources.ts",
    "./semantic-platform-tag-resources.ts",
  ],
  semanticPlatformDetails: ["./semantic-platform-details-resources.ts"],
  semanticPlatformAssets: ["./semantic-platform-assets-resources.ts"],
  semanticPlatformGovernance: ["./semantic-platform-governance-resources.ts"],
  project: ["./project-resources.ts"],
  projectWorkspace: [
    "./project-workspace-agent-resources.ts",
    "./project-workspace-run-resources.ts",
    "./project-workspace-checklist-select-resources.ts",
    "./project-workspace-checklist-lib-resources.ts",
    "./project-workspace-business-resources.ts",
    "./project-workspace-settings-resources.ts",
    "./project-workspace-insight-resources.ts",
    "./project-workspace-formulation-resources.ts",
    "./project-workspace-sheet-filling-resources.ts",
    "./project-workspace-manday-resources.ts",
    "./project-workspace-checklist-execution-resources.ts",
    "./project-workspace-files-resources.ts",
    "./project-workspace-organize-resources.ts",
    "./project-workspace-quota-resources.ts",
    "./project-workspace-quota-presentation-resources.ts",
    "./project-workspace-quota-rule-resources.ts",
    "./project-workspace-resources.ts",
  ],
  sharedUi: ["./shared-ui-resources.ts"],
  wordEditor: [
    "./word-editor-shell-resources.ts",
    "./word-editor-controls-resources.ts",
  ],
  pptEditor: ["./ppt-editor-resources.ts"],
};

const resourceImporters = import.meta.glob<ResourceModule>("./*-resources.ts");
const localeNames: readonly UiLocale[] = ["zh-CN", "en-US"];
const namespaceResourceCache = new Map<
  LazyNamespace,
  Promise<Record<UiLocale, ResourceRecord>>
>();

function isRecord(value: unknown): value is ResourceRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isBilingualResource(value: unknown): value is BilingualResource {
  if (!isRecord(value)) return false;
  return localeNames.every((locale) => isRecord(value[locale]));
}

function mergeResource(target: ResourceRecord, source: ResourceRecord): void {
  for (const [key, value] of Object.entries(source)) {
    const existing = target[key];
    if (isRecord(existing) && isRecord(value)) {
      mergeResource(existing, value);
    } else {
      target[key] = value;
    }
  }
}

async function loadNamespaceResources(
  namespace: LazyNamespace,
): Promise<Record<UiLocale, ResourceRecord>> {
  const cached = namespaceResourceCache.get(namespace);
  if (cached) return cached;

  const paths = namespaceResourceModules[namespace];

  const loading = Promise.all(
    paths.map(async (path) => {
      const importer = resourceImporters[path];
      if (!importer)
        throw new Error("Missing i18n resource module " + path + ".");
      const module = await importer();
      const resource = Object.values(module).find(isBilingualResource);
      if (!resource)
        throw new Error("No bilingual resource export found in " + path + ".");
      return resource;
    }),
  ).then((resources) => {
    const result: Record<UiLocale, ResourceRecord> = {
      "zh-CN": {},
      "en-US": {},
    };
    for (const resource of resources) {
      for (const locale of localeNames) {
        const bundle = resource[locale][namespace];
        if (isRecord(bundle)) mergeResource(result[locale], bundle);
      }
    }
    return result;
  });

  namespaceResourceCache.set(namespace, loading);
  void loading.catch(() => {
    if (namespaceResourceCache.get(namespace) === loading)
      namespaceResourceCache.delete(namespace);
  });
  return loading;
}

export const uiResourceBackend: BackendModule = {
  type: "backend" as const,
  init: () => undefined,
  read(language, namespace, callback) {
    if (language !== "zh-CN" && language !== "en-US") {
      callback(new Error("Unsupported UI locale " + language + "."), false);
      return;
    }
    if (!(namespace in namespaceResourceModules)) {
      callback(
        new Error("Unknown i18n resource namespace " + namespace + "."),
        false,
      );
      return;
    }
    void loadNamespaceResources(namespace as LazyNamespace).then(
      (resources) => callback(null, resources[language]),
      (error: unknown) =>
        callback(error instanceof Error ? error : String(error), false),
    );
  },
};
